import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useLandscapeLayout } from './useLandscapeLayout';

/** Cap for game/course metadata beside the trainer board (landscape). */
export const TRAINER_INFO_PANEL_MAX_WIDTH = {
  xs: 420,
  xxl: 480,
  xxxl: 540,
} as const;

const TrainerPanelMeasureContext =
  createContext<React.RefObject<HTMLDivElement> | null>(null);

/** Full-width shell ref for responsive board sizing inside {@link TrainerPanelLayout}. */
export const useTrainerPanelMeasureRef = () =>
  useContext(TrainerPanelMeasureContext);

type TrainerPanelLayoutProps = {
  info?: React.ReactNode;
  children: React.ReactNode;
  /** Stretch to fill a flex parent (review page on tablet). */
  fillHeight?: boolean;
};

const fillHeightSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden' as const,
};

type LandscapeColumns = {
  outerLeft: number;
  info: number;
  right: number;
};

/**
 * Trainer shell: stacked info above the board in portrait; in landscape info
 * sits in a LEFT column beside the board.
 *
 * LANDSCAPE BOARD PLACEMENT: board+controls size the middle track. Remaining
 * width first expands the fixed-per-layout info track up to
 * {@link TRAINER_INFO_PANEL_MAX_WIDTH}; any further space fills the right
 * gutter until the board is centered, then excess is balanced on both sides.
 * The panel width is derived from layout geometry, not line content. Pair with
 * {@link TRAINER_SIDE_COLUMN_MIN_RESERVE_PX} on BOTH sides in
 * {@link useFittedBoardWidth} so the middle track leaves usable gutters.
 */
export const TrainerPanelLayout = ({
  info,
  children,
  fillHeight = false,
}: TrainerPanelLayoutProps) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const boardRegionRef = useRef<HTMLDivElement>(null);
  const boardColumnRef = useRef<HTMLDivElement>(null);
  const [landscapeColumns, setLandscapeColumns] =
    useState<LandscapeColumns | null>(null);
  const hasSidebar = Boolean(info);
  const isLandscape = useLandscapeLayout();
  const theme = useTheme();
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWide = useMediaQuery(theme.breakpoints.up('xxl'));
  const infoMaxWidth = isUltraWide
    ? TRAINER_INFO_PANEL_MAX_WIDTH.xxxl
    : isWide
      ? TRAINER_INFO_PANEL_MAX_WIDTH.xxl
      : TRAINER_INFO_PANEL_MAX_WIDTH.xs;

  useLayoutEffect(() => {
    if (!hasSidebar || !isLandscape) {
      setLandscapeColumns(null);
      return;
    }

    const region = boardRegionRef.current;
    const boardColumn = boardColumnRef.current;
    if (!region || !boardColumn) return;

    const updateColumns = () => {
      const available =
        region.getBoundingClientRect().width -
        boardColumn.getBoundingClientRect().width;
      const spare = Math.max(0, available);
      const infoWidth = Math.min(infoMaxWidth, spare);

      // Before the info reaches its cap it gets all spare width. Afterwards,
      // grow the right gutter until it matches the full left side; once
      // centered, split additional space equally.
      const leftTotal =
        spare <= infoMaxWidth
          ? spare
          : spare < infoMaxWidth * 2
            ? infoMaxWidth
            : spare / 2;
      const right = Math.max(0, spare - leftTotal);
      const outerLeft = Math.max(0, leftTotal - infoWidth);

      setLandscapeColumns((current) => {
        const next = {
          outerLeft: Math.round(outerLeft),
          info: Math.round(infoWidth),
          right: Math.round(right),
        };
        return current?.outerLeft === next.outerLeft &&
          current.info === next.info &&
          current.right === next.right
          ? current
          : next;
      });
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(region);
    observer.observe(boardColumn);
    return () => observer.disconnect();
  }, [hasSidebar, infoMaxWidth, isLandscape]);

  return (
    <TrainerPanelMeasureContext.Provider value={measureRef}>
      <Box
        ref={measureRef}
        sx={{
          width: '100%',
          // Prevent flex min-content blowout from an oversized board's first paint
          // so getBoundingClientRect width stays viewport-bound on iPad portrait.
          minWidth: 0,
          ...(fillHeight ? fillHeightSx : {}),
        }}
      >
        {hasSidebar && !isLandscape ? (
          <Box
            sx={{
              display: 'grid',
              rowGap: 1.5,
              alignItems: 'start',
              mb: 2,
              width: '100%',
              maxWidth: {
                xs: TRAINER_INFO_PANEL_MAX_WIDTH.xs,
                sm: '100%',
              },
              mx: { xs: 'auto', sm: 0 },
              px: 1,
              gridTemplateColumns: 'minmax(0, 1fr)',
            }}
          >
            <Box minWidth={0}>{info}</Box>
          </Box>
        ) : null}

        <Box
          ref={boardRegionRef}
          data-trainer-board-region
          sx={{
            display: 'grid',
            width: '100%',
            alignItems: fillHeight ? 'center' : 'start',
            alignContent: fillHeight ? 'center' : 'start',
            minHeight: fillHeight ? '100%' : undefined,
            // Before measurement, use equal side tracks. Once measured, give
            // spare width to info first, then center when enough room remains.
            gridTemplateColumns:
              hasSidebar && isLandscape
                ? landscapeColumns
                  ? `${landscapeColumns.outerLeft}px ${landscapeColumns.info}px max-content ${landscapeColumns.right}px`
                  : '0 minmax(0, 1fr) max-content minmax(0, 1fr)'
                : 'minmax(0, 1fr)',
            ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
          }}
        >
          {hasSidebar && isLandscape ? (
            <Box
              sx={{
                gridColumn: 2,
                justifySelf: 'end',
                // Fill the column height so panels can pin content to the top;
                // centered flex keeps fixed-height panels vertically centered.
                alignSelf: fillHeight ? 'stretch' : 'start',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: fillHeight ? 'center' : 'flex-start',
                minWidth: 0,
                minHeight: 0,
                // Fill the leftover left track (from board+controls sizing);
                // maxWidth only caps ultra-wide gutters — not a fixed panel size.
                width: '100%',
                maxWidth: TRAINER_INFO_PANEL_MAX_WIDTH,
                boxSizing: 'border-box',
                pl: 1,
                pr: 3,
                pt: 0.5,
              }}
            >
              {info}
            </Box>
          ) : null}

          <Box
            ref={boardColumnRef}
            sx={{
              gridColumn: hasSidebar && isLandscape ? 3 : 1,
              justifySelf: 'center',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              ...(fillHeight
                ? {
                    ...fillHeightSx,
                    justifyContent: 'center',
                  }
                : {}),
            }}
          >
            {children}
          </Box>

          {hasSidebar && isLandscape ? (
            <Box aria-hidden sx={{ gridColumn: 4, minWidth: 0 }} />
          ) : null}
        </Box>
      </Box>
    </TrainerPanelMeasureContext.Provider>
  );
};
